import { useState, useEffect, useMemo, useCallback } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router";
import { Controller, useFormContext } from "react-hook-form";
import { IoChevronDown, IoChevronUp } from "react-icons/io5";
import { Button, ContentCard, FormField, Loader, Checkbox } from "../../../components";
import { ModuleLayout } from "../../../layouts";
import {
  registerCompanyApi,
  updateCompanyApi,
  getCompanyByIdApi,
  getModulesApi,
} from "../../../DAL";
import { ROUTES_CONSTANTS, QUERY_CONSTANTS } from "../../../constants";
import { enqueueSnackbar } from "notistack";
import { useAuth } from "../../../context";

// ─── Section header ──────────────────────────────────────────────────────────
const SectionHeader = ({ title, subtitle }) => (
  <div className="mb-5">
    <h2 className="text-xl font-medium text-[#666]">{title}</h2>
    {subtitle && <p className="text-xs text-[#666] mt-1">{subtitle}</p>}
  </div>
);

// ─── Select options ───────────────────────────────────────────────────────────
const COMPANY_SIZE_OPTIONS = [
  // { value: "1-10", label: "1–10" },
  // { value: "11-50", label: "11–50" },
  // { value: "51-200", label: "51–200" },
  // { value: "201-500", label: "201–500" },
  // { value: "500+", label: "500+" },
  { value: "small", label: "Small (1–50 employees)" },
  { value: "medium", label: "Medium (51–200 employees)" },
  { value: "large", label: "Large (201+ employees)" },
];

const COUNTRY_OPTIONS = [
  { value: "US", label: "United States" },
  { value: "GB", label: "United Kingdom" },
  { value: "CA", label: "Canada" },
  { value: "AU", label: "Australia" },
  { value: "DE", label: "Germany" },
  { value: "FR", label: "France" },
  { value: "ES", label: "Spain" },
  { value: "MX", label: "Mexico" },
  { value: "BR", label: "Brazil" },
  { value: "IN", label: "India" },
  { value: "CN", label: "China" },
  { value: "JP", label: "Japan" },
  { value: "PK", label: "Pakistan" },
];

const ADMIN_ROLE_OPTIONS = [
  { value: "admin", label: "Admin" },
  { value: "manager", label: "Manager" },
  { value: "analyst", label: "Analyst" },
  { value: "viewer", label: "Viewer" },
];

const ADMIN_DEPT_OPTIONS = [
  { value: "engineering", label: "Engineering" },
  { value: "hr", label: "Human Resources" },
  { value: "marketing", label: "Marketing" },
  { value: "finance", label: "Finance" },
  { value: "operations", label: "Operations" },
  { value: "qa", label: "Quality Assurance" },
  { value: "rd", label: "R&D" },
];

const ADMIN_STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "pending", label: "Pending" },
];


// ─── Module card (dynamic from API) ──────────────────────────────────────────
const ModuleCard = ({ module }) => {
  const { control, watch, setValue } = useFormContext();
  const [isOpen, setIsOpen] = useState(true);
  const moduleKey = `modules.module_${module.id}`;
  const enabled = watch(`${moduleKey}.enabled`);

  const toggleParent = () => {
    const next = !enabled;
    setValue(`${moduleKey}.enabled`, next, { shouldValidate: true });
    setValue(`${moduleKey}.module_id`, module.id);
    setValue(`${moduleKey}.module_name`, module.name);
    setValue(`${moduleKey}.module_category`, module.code);
    (module.features || []).forEach((f) =>
      setValue(`${moduleKey}.feature_${f.id}`, next, { shouldValidate: true }),
    );
  };

  return (
    <div style={{ width: 210 }}>
      <div
        className="flex items-center gap-2 mb-2 cursor-pointer select-none"
        onClick={() => setIsOpen((o) => !o)}
      >
        <Checkbox checked={enabled} onChange={toggleParent} />
        <span className="flex-1 text-sm font-semibold text-black">
          {module.name}
        </span>
        {isOpen ? (
          <IoChevronUp className="w-4 h-4 text-gray-400" />
        ) : (
          <IoChevronDown className="w-4 h-4 text-gray-400" />
        )}
      </div>

      {isOpen && module.features?.length > 0 && (
        <div className="border border-[#ccc] rounded-lg px-3 py-3 space-y-3">
          {module.features.map((feature) => (
            <Controller
              key={feature.id}
              control={control}
              name={`${moduleKey}.feature_${feature.id}`}
              render={({ field }) => (
                <div
                  className="cursor-pointer select-none"
                  style={{
                    display: "grid",
                    gridTemplateColumns: "16px 1fr",
                    gap: 8,
                    alignItems: "start",
                  }}
                  onClick={() => field.onChange(!field.value)}
                >
                  <Checkbox
                    checked={field.value}
                    onChange={() => field.onChange(!field.value)}
                    className="mt-0.5"
                  />
                  <div>
                    <p className="text-[11px] font-medium text-[#666] leading-snug">
                      {feature.name}
                    </p>
                    <p className="text-[10px] text-[#999] leading-snug">
                      {feature.description}
                    </p>
                  </div>
                </div>
              )}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Confirm Modal ────────────────────────────────────────────────────────────
const ConfirmModal = ({ data, onConfirm, onCancel, isPending, isEdit, t }) => (
  <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
    <div className="bg-white rounded-xl shadow-[0px_2px_4px_0px_rgba(0,0,0,0.25)] w-full max-w-lg mx-4 overflow-hidden border-2 border-primary-500">
      <div className="bg-primary-500 px-6 py-4">
        <h2 className="text-white text-lg font-semibold text-center">
          {isEdit ? "Confirm Company Update" : "Confirm Company Registration"}
        </h2>
      </div>
      <div className="px-6 py-5">
        <p className="text-sm text-[#666] text-center mb-5">
          {isEdit
            ? "Please review the details below. Once submitted, the company information will be updated."
            : "Please review the details below. Once submitted, the company will be registered and access will be granted based on selected modules."}
        </p>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs font-medium text-[#666]">Company Name</p>
            <p className="text-sm text-[#999] mt-0.5">
              {data?.companyName || "—"}
            </p>
          </div>
          <div>
            <p className="text-xs font-medium text-[#666]">Admin Name</p>
            <p className="text-sm text-[#999] mt-0.5">
              {data?.adminFullName || "—"}
            </p>
          </div>
        </div>
      </div>
      <div className="px-6 pb-5 flex items-center gap-3">
        <button
          onClick={onCancel}
          className="flex-1 border border-[#ccc] rounded-md py-2.5 text-sm text-[#666] hover:bg-gray-50 transition cursor-pointer"
        >
          Cancel
        </button>
        <button
          onClick={() => onConfirm(data)}
          disabled={isPending}
          className="flex-1 bg-primary-500 rounded-md py-2.5 text-sm text-white font-medium hover:bg-primary-600 transition disabled:opacity-60 cursor-pointer"
        >
          {isPending ? t("registerCompany.btnLoading") : "Confirm"}
        </button>
      </div>
    </div>
  </div>
);

// ─── Build FormData for register API ──────────────────────────────────────────
const buildFormData = (data, modules) => {
  const fd = new FormData();

  fd.append("company_name", data.companyName);
  fd.append("legal_name", data.legalName);
  fd.append("registration_number", data.registrationNumber);
  fd.append("tax_id", data.taxId);
  fd.append("vat_number", data.vatNumber || "");
  fd.append("company_size", data.companySize);
  fd.append("description", data.companyDescription);
  fd.append("street_address", data.streetAddress);
  fd.append("country", data.country);
  fd.append("city", data.city);
  fd.append("state_province", data.stateProvince);
  fd.append("zip_postal_code", data.zipPostalCode);
  fd.append("contact_email", data.contactEmail);
  fd.append("contact_phone", data.contactPhone);
  fd.append("website", data.website);
  fd.append("admin_full_name", data.adminFullName);
  fd.append("admin_email", data.adminEmail);
  fd.append("admin_role", data.adminRole);
  fd.append("admin_department", data.adminDepartment);

  if (data.companyLogo instanceof File) {
    fd.append("logo", data.companyLogo);
  }

  // Collect selected modules with their features
  let moduleIndex = 0;
  if (data.modules && modules) {
    Object.entries(data.modules).forEach(([, val]) => {
      if (val.enabled) {
        const mod = modules.find((m) => m.id === val.module_id);
        if (mod) {
          const selectedFeatures = (mod.features || []).filter(
            (f) => val[`feature_${f.id}`],
          );
          if (selectedFeatures.length > 0) {
            selectedFeatures.forEach((f) => {
              fd.append(`modules[${moduleIndex}][module_id]`, mod.id);
              fd.append(`modules[${moduleIndex}][module_name]`, mod.name);
              fd.append(`modules[${moduleIndex}][module_category]`, mod.code);
              fd.append(`modules[${moduleIndex}][feature_id]`, f.id);
              fd.append(`modules[${moduleIndex}][feature_name]`, f.name);
              moduleIndex++;
            });
          } else {
            fd.append(`modules[${moduleIndex}][module_id]`, mod.id);
            fd.append(`modules[${moduleIndex}][module_name]`, mod.name);
            fd.append(`modules[${moduleIndex}][module_category]`, mod.code);
            moduleIndex++;
          }
        }
      }
    });
  }

  return fd;
};

// ─── Main component ───────────────────────────────────────────────────────────
const CompanyForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { currentUser } = useAuth();
  const isEdit = location.pathname === ROUTES_CONSTANTS.UPDATE_COMPANY;
  const companyId = isEdit ? currentUser?.company?.id : null;
  const [confirmData, setConfirmData] = useState(null);

  // ── Fetch modules from API ──────────────────────────────────────────────────
  const { data: modulesData } = useQuery({
    queryKey: [QUERY_CONSTANTS.MODULES],
    queryFn: getModulesApi,
  });

  const modules = modulesData?.data?.modules || [];

  // ── Fetch company data for edit mode ────────────────────────────────────────
  const { data: companyData, isLoading: isLoadingCompany } = useQuery({
    queryKey: [QUERY_CONSTANTS.COMPANY, companyId],
    queryFn: async () => {
      const response = await getCompanyByIdApi(companyId);
      return response.data.company;
    },
    enabled: isEdit,
  });

  // ── Validation schema ──────────────────────────────────────────────────────
  const schema = useMemo(
    () =>
      yup.object({
        companyName: yup.string().required(t("validation.required")),
        legalName: yup.string().required(t("validation.required")),
        registrationNumber: yup.string().required(t("validation.required")),
        taxId: yup.string().required(t("validation.required")),
        vatNumber: yup.string(),
        companySize: yup.string().required(t("validation.required")),
        companyDescription: yup.string().required(t("validation.required")),
        companyLogo: yup.mixed().nullable(),

        streetAddress: yup.string().required(t("validation.required")),
        country: yup.string().required(t("validation.required")),
        city: yup.string().required(t("validation.required")),
        stateProvince: yup.string().required(t("validation.required")),
        zipPostalCode: yup.string().required(t("validation.required")),

        contactEmail: yup
          .string()
          .required(t("validation.emailRequired"))
          .matches(
            /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
            t("validation.emailInvalid"),
          ),
        contactPhone: yup.string().required(t("validation.required")),
        website: yup
          .string()
          .required(t("validation.required"))
          .test("url-or-empty", t("validation.urlInvalid"), (v) => {
            if (!v) return true;
            try {
              new URL(v);
              return true;
            } catch {
              return false;
            }
          }),

        adminFullName: yup.string().required(t("validation.required")),
        adminEmail: yup
          .string()
          .required(t("validation.emailRequired"))
          .matches(
            /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
            t("validation.emailInvalid"),
          ),
        adminRole: yup.string().required(t("validation.required")),
        adminDepartment: yup.string().required(t("validation.required")),
        adminStatus: yup.string().required(t("validation.required")),

        modules: yup.object(),
      }),
    [t],
  );

  // ── Build default module values ─────────────────────────────────────────────
  const buildModuleDefaults = useCallback(
    (companyModules) => {
      const defaults = {};
      modules.forEach((mod) => {
        const key = `module_${mod.id}`;
        const isAssigned = companyModules?.some(
          (cm) => cm.module_id === mod.id,
        );
        defaults[key] = {
          enabled: isAssigned || false,
          module_id: mod.id,
          module_name: mod.name,
          module_category: mod.code,
        };
        (mod.features || []).forEach((f) => {
          defaults[key][`feature_${f.id}`] = companyModules?.some(
            (cm) => cm.module_id === mod.id && cm.feature_id === f.id,
          ) || false;
        });
      });
      return defaults;
    },
    [modules],
  );

  // ── Form setup ─────────────────────────────────────────────────────────────
  const methods = useForm({
    resolver: yupResolver(schema),
    mode: "onBlur",
    defaultValues: {
      companyName: "",
      legalName: "",
      registrationNumber: "",
      taxId: "",
      vatNumber: "",
      companySize: "",
      companyDescription: "",
      companyLogo: null,
      streetAddress: "",
      country: "",
      city: "",
      stateProvince: "",
      zipPostalCode: "",
      contactEmail: "",
      contactPhone: "",
      website: "",
      adminFullName: "",
      adminEmail: "",
      adminRole: "",
      adminDepartment: "",
      adminStatus: "",
      modules: {},
    },
  });

  // ── Populate form for edit mode ─────────────────────────────────────────────
  useEffect(() => {
    if (!isEdit || !companyData) return;
    const c = companyData;
    const admin = c.administrators?.[0] || {};

    methods.reset({
      companyName: c.company_name || "",
      legalName: c.legal_name || "",
      registrationNumber: c.registration_number || "",
      taxId: c.tax_id || "",
      vatNumber: c.vat_number || "",
      companySize: c.company_size || "",
      companyDescription: c.description || "",
      companyLogo: c.logo_path || null,
      streetAddress: c.address?.street_address || "",
      country: c.address?.country || "",
      city: c.address?.city || "",
      stateProvince: c.address?.state_province || "",
      zipPostalCode: c.address?.zip_postal_code || "",
      contactEmail: c.contact?.contact_email || "",
      contactPhone: c.contact?.contact_phone || "",
      website: c.contact?.website || "",
      adminFullName: admin.full_name || "",
      adminEmail: admin.email || "",
      adminRole: admin.role || "",
      adminDepartment: admin.department || "",
      adminStatus: admin.status || "",
      modules: buildModuleDefaults(c.modules),
    });
  }, [isEdit, companyData, methods, buildModuleDefaults]);

  // ── Set module defaults when modules load (register mode) ───────────────────
  useEffect(() => {
    if (isEdit || modules.length === 0) return;
    methods.setValue("modules", buildModuleDefaults([]));
  }, [isEdit, modules, methods, buildModuleDefaults]);

  // ── Mutation ───────────────────────────────────────────────────────────────
  const { mutate: submitCompany, isPending } = useMutation({
    mutationFn: (data) => {
      const fd = buildFormData(data, modules);
      if (isEdit) {
        return updateCompanyApi(companyId, fd);
      }
      return registerCompanyApi(fd);
    },
    onSuccess: (response) => {
      enqueueSnackbar(response?.message, { variant: "success" });
      setConfirmData(null);
      queryClient.invalidateQueries({
        queryKey: [QUERY_CONSTANTS.CURRENT_USER],
      });
      if (isEdit) {
        queryClient.invalidateQueries({
          queryKey: [QUERY_CONSTANTS.COMPANY],
        });
      }
      navigate(ROUTES_CONSTANTS.COMPANY_DETAIL);
    },
  });

  const onSubmit = (data) => setConfirmData(data);
  const handleConfirm = (data) => submitCompany(data);

  // ── Loading state for edit ──────────────────────────────────────────────────
  if (isEdit && isLoadingCompany) {
    return (
      <ModuleLayout
        title={t("companyManagement.title")}
        onBack={() => navigate(ROUTES_CONSTANTS.COMPANY_MANAGEMENT)}
      >
        <div className="flex items-center justify-center h-full">
          <Loader fullPage={false} />
        </div>
      </ModuleLayout>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      {confirmData && (
        <ConfirmModal
          data={confirmData}
          onConfirm={handleConfirm}
          onCancel={() => setConfirmData(null)}
          isPending={isPending}
          isEdit={isEdit}
          t={t}
        />
      )}
      <ModuleLayout
        title={t("companyManagement.title")}
        onBack={() => navigate(ROUTES_CONSTANTS.COMPANY_MANAGEMENT)}
      >
        <ContentCard className="space-y-8">
          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)} noValidate>
              {/* ── TOP: Company Info (left) + Address/Contact (right) ─────── */}
              <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-0">
                {/* Left column ─ Company Information */}
                <div className="lg:pr-8 lg:border-r border-[#e3e3e3] pb-8 lg:pb-0 space-y-4">
                  <SectionHeader
                    title={t("registerCompany.companyInfo")}
                    subtitle={t("registerCompany.companyInfoSubtitle")}
                  />

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <FormField
                      name="companyName"
                      label={t("registerCompany.companyName")}
                      placeholder={t("registerCompany.companyNamePlaceholder")}
                    />
                    <FormField
                      name="legalName"
                      label={t("registerCompany.legalName")}
                      placeholder={t("registerCompany.legalNamePlaceholder")}
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <FormField
                      name="registrationNumber"
                      label={t("registerCompany.registrationNumber")}
                      placeholder={t(
                        "registerCompany.registrationNumberPlaceholder",
                      )}
                    />
                    <FormField
                      name="taxId"
                      label={t("registerCompany.taxId")}
                      placeholder={t("registerCompany.taxIdPlaceholder")}
                    />
                    <FormField
                      name="companySize"
                      type="select"
                      label={t("registerCompany.companySize")}
                      placeholder={t("registerCompany.companySizePlaceholder")}
                      options={COMPANY_SIZE_OPTIONS}
                    />
                  </div>

                  <FormField
                    name="companyDescription"
                    type="textarea"
                    rows={4}
                    label={t("registerCompany.companyDescription")}
                    placeholder={t(
                      "registerCompany.companyDescriptionPlaceholder",
                    )}
                  />

                  <FormField
                    name="companyLogo"
                    type="file"
                    label={t("registerCompany.companyLogo")}
                  />
                </div>

                {/* Right column ─ Address + Contact */}
                <div className="lg:pl-8 pt-8 lg:pt-0 space-y-8">
                  {/* Address Information */}
                  <div className="space-y-4">
                    <SectionHeader
                      title={t("registerCompany.addressInfo")}
                      subtitle={t("registerCompany.addressInfoSubtitle")}
                    />
                    <FormField
                      name="streetAddress"
                      label={t("registerCompany.streetAddress")}
                      placeholder={t(
                        "registerCompany.streetAddressPlaceholder",
                      )}
                    />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <FormField
                        name="country"
                        type="select"
                        label={t("registerCompany.country")}
                        placeholder={t("registerCompany.countryPlaceholder")}
                        options={COUNTRY_OPTIONS}
                      />
                      <FormField
                        name="city"
                        label={t("registerCompany.city")}
                        placeholder={t("registerCompany.cityPlaceholder")}
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <FormField
                        name="stateProvince"
                        label={t("registerCompany.stateProvince")}
                        placeholder={t(
                          "registerCompany.stateProvincePlaceholder",
                        )}
                      />
                      <FormField
                        name="zipPostalCode"
                        label={t("registerCompany.zipPostalCode")}
                        placeholder={t(
                          "registerCompany.zipPostalCodePlaceholder",
                        )}
                      />
                    </div>
                  </div>

                  {/* Contact Information */}
                  <div className="space-y-4">
                    <SectionHeader
                      title={t("registerCompany.contactInfo")}
                      subtitle={t("registerCompany.contactInfoSubtitle")}
                    />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <FormField
                        name="contactEmail"
                        type="email"
                        label={t("registerCompany.contactEmail")}
                        placeholder={t(
                          "registerCompany.contactEmailPlaceholder",
                        )}
                      />
                      <FormField
                        name="contactPhone"
                        type="tel"
                        label={t("registerCompany.contactPhone")}
                        placeholder={t(
                          "registerCompany.contactPhonePlaceholder",
                        )}
                      />
                    </div>
                    <FormField
                      name="website"
                      type="url"
                      label={t("registerCompany.website")}
                      placeholder={t("registerCompany.websitePlaceholder")}
                    />
                  </div>
                </div>
              </div>

              {/* ── Application Administrator ──────────────────────────────── */}
              <div className="pt-8 mt-8 border-t border-[#e3e3e3] space-y-4">
                <SectionHeader
                  title={t("registerCompany.appAdmin")}
                  subtitle={t("registerCompany.appAdminSubtitle")}
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField
                    name="adminFullName"
                    label={t("registerCompany.adminFullName")}
                    placeholder={t("registerCompany.adminFullNamePlaceholder")}
                  />
                  <FormField
                    name="adminEmail"
                    type="email"
                    label={t("registerCompany.adminEmail")}
                    placeholder={t("registerCompany.adminEmailPlaceholder")}
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <FormField
                    name="adminRole"
                    type="select"
                    label={t("registerCompany.adminRole")}
                    placeholder={t("registerCompany.adminRolePlaceholder")}
                    options={ADMIN_ROLE_OPTIONS}
                  />
                  <FormField
                    name="adminDepartment"
                    type="select"
                    label={t("registerCompany.adminDepartment")}
                    placeholder={t(
                      "registerCompany.adminDepartmentPlaceholder",
                    )}
                    options={ADMIN_DEPT_OPTIONS}
                  />
                  <FormField
                    name="adminStatus"
                    type="select"
                    label={t("registerCompany.adminStatus")}
                    placeholder={t("registerCompany.adminStatusPlaceholder")}
                    options={ADMIN_STATUS_OPTIONS}
                  />
                </div>
              </div>

              {/* ── Assign Module ──────────────────────────────────────────── */}
              <div className="pt-8 mt-8 border-t border-[#e3e3e3] space-y-4">
                <SectionHeader
                  title={t("registerCompany.assignModule")}
                  subtitle={t("registerCompany.assignModuleSubtitle")}
                />
                <div className="flex flex-wrap gap-4">
                  {modules.map((mod) => (
                    <ModuleCard key={mod.id} module={mod} />
                  ))}
                </div>
              </div>

              {/* ── Submit ─────────────────────────────────────────────────── */}
              <div className="flex justify-end mt-8">
                <Button type="submit" loading={isPending} size="lg">
                  {isPending
                    ? t(isEdit ? "registerCompany.btnUpdating" : "registerCompany.btnLoading")
                    : t(isEdit ? "registerCompany.btnUpdate" : "registerCompany.btnSubmit")}
                </Button>
              </div>
            </form>
          </FormProvider>
        </ContentCard>
      </ModuleLayout>
    </>
  );
};

export default CompanyForm;
